import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';

const formSchema = z.object({
	fullName: z.string().min(2, 'Name is required'),
	phone: z.string().min(10, 'Valid phone number required'),
	email: z.string().email('Valid email required'),
	location: z.object({
		city: z.string().min(2, 'City is required'),
		state: z.string().min(2, 'State/Province is required'),
		country: z.string().min(2, 'Country is required'),
	}),
	coachingReason: z.string().min(1, 'Please share why you need coaching'),
	challenges: z.string().min(1, 'Please describe your challenges'),
	currentRole: z.string().min(1, 'Current role is required'),
	publicSpeakingGoal: z.string().min(1, 'Please select a goal'),
});

interface PersonalInfoFormProps {
	data: z.infer<typeof formSchema>;
	onSubmit: (data: z.infer<typeof formSchema>) => void;
	onBack: () => void;
	isSubmitting: boolean;
}

export function PersonalInfoForm({
	data,
	onSubmit,
	onBack,
	isSubmitting,
}: PersonalInfoFormProps) {
	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: data,
	});

	return (
		<div className='space-y-6 w-full max-w-3xl'>
			<SheetHeader className='p-0'>
				<SheetTitle>Personal Information</SheetTitle>
				<p className='text-sm text-gray-500'>Please fill in your details</p>
			</SheetHeader>

			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} className='space-y-8'>
					<div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
						<FormField
							control={form.control}
							name='fullName'
							render={({ field }) => (
								<FormItem>
									<FormLabel>Full Name</FormLabel>
									<FormControl>
										<Input placeholder='John Doe' {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name='email'
							render={({ field }) => (
								<FormItem>
									<FormLabel>Email</FormLabel>
									<FormControl>
										<Input
											type='email'
											placeholder='john@example.com'
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name='phone'
							render={({ field }) => (
								<FormItem>
									<FormLabel>Phone Number</FormLabel>
									<FormControl>
										<Input
											type='tel'
											placeholder='+27 12 345 6789'
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					<div className='space-y-4'>
						<div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
							<FormField
								control={form.control}
								name='location.city'
								render={({ field }) => (
									<FormItem>
										<FormLabel>City</FormLabel>
										<FormControl>
											<Input placeholder='Cape Town' {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name='location.state'
								render={({ field }) => (
									<FormItem>
										<FormLabel>State/Province</FormLabel>
										<FormControl>
											<Input placeholder='Western Cape' {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name='location.country'
								render={({ field }) => (
									<FormItem>
										<FormLabel>Country</FormLabel>
										<FormControl>
											<Input placeholder='South Africa' {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className='space-y-6 pt-6 border-t'>
						<FormField
							control={form.control}
							name='coachingReason'
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										Why do you believe you need public speaking coaching?
									</FormLabel>
									<FormControl>
										<Textarea
											placeholder='Please share your reasons for seeking coaching...'
											className='min-h-[100px]'
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name='challenges'
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										What challenges/areas of concern would you like to address?
									</FormLabel>
									<FormControl>
										<Textarea
											placeholder='Describe the challenges you face with public speaking...'
											className='min-h-[100px]'
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name='currentRole'
							render={({ field }) => (
								<FormItem>
									<FormLabel>What is your current role at work?</FormLabel>
									<FormControl>
										<Input placeholder='e.g. Marketing Manager' {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name='publicSpeakingGoal'
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										Briefly describe one public speaking goal regarding your
										career in the next 6 months
									</FormLabel>
									<Select
										onValueChange={field.onChange}
										defaultValue={field.value}
									>
										<FormControl>
											<SelectTrigger>
												<SelectValue placeholder='Select a goal' />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											<SelectItem value='lead_forums'>
												I'd like to lead group forums
											</SelectItem>
											<SelectItem value='annual_conference'>
												I'd like to speak at the annual conference
											</SelectItem>
											<SelectItem value='presentations'>
												I'd like to improve my presentation skills
											</SelectItem>
											<SelectItem value='team_meetings'>
												I'd like to speak confidently in team meetings
											</SelectItem>
											<SelectItem value='client_pitches'>
												I'd like to deliver better client pitches
											</SelectItem>
											<SelectItem value='other'>
												Other (please specify in challenges)
											</SelectItem>
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					<div className='flex justify-between pt-6 border-t'>
						<Button
							type='button'
							variant='outline'
							onClick={onBack}
							disabled={isSubmitting}
						>
							Back
						</Button>
						<Button
							type='submit'
							className='bg-[#fbb040] hover:bg-[#f8c880]'
							disabled={isSubmitting}
						>
							{isSubmitting ? (
								<>
									<Loader2 className='mr-2 h-4 w-4 animate-spin' />
									Processing...
								</>
							) : (
								'Continue'
							)}
						</Button>
					</div>
				</form>
			</Form>
		</div>
	);
}
